// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — what a shared session does to this device
 * (`docs/SHARED_AR_PLAN.md`): the lead's sphere is described a few times
 * a second, and every follower makes its own sphere match.
 *
 * What travels: which dataset is on the sphere, where its playhead is,
 * the layers around it, where the browser globe's camera looks, and how
 * the sphere is turned and sized in AR. Each follower loads
 * the dataset and decodes the video itself; only the description crosses
 * the network, so a room costs a few hundred bytes a second however large
 * the stream is.
 *
 * The app is reached through `RoomSyncHost`, so this module knows nothing
 * of `main.ts` or the AR session, and the decisions with a wrong answer
 * available are pure functions:
 *
 *   - `shouldSend`: the lead speaks when something changed, or once a
 *     second regardless. A steadily playing video is not a change: a
 *     follower can predict it, so only a playhead that is not where the
 *     last message implies (a seek, a loop, a stall) is worth sending.
 *   - `followPlayback`: a follower closes a gap by playing a little
 *     faster or slower, and seeks only when it is seconds out. A seek
 *     stalls the decoder: the first version seeked at 0.6 s, and on a
 *     phone each seek cost more than that, so the follower fell behind
 *     again while seeking and the dataset played in jerks. A rate trim
 *     costs nothing and converges.
 *
 * A follower's own controls are not disabled; whatever it changes is put
 * back by the lead's next message. Passing the lead is the room's
 * business (`workers/rooms`): the longest-connected device leads.
 *
 * While the lead runs an **explore** activity (`roomActivity.ts`), the
 * follower's sphere is its own (owner, 2026-10-12: "full control of the
 * sphere and the Tools panel"): the lead's view, dataset, time, layers,
 * globes and AR pose are not applied, and a locked audience gets every
 * control back (`setExploring(true, true)`). When the activity ends, or
 * its time is up on this device's clock, the controls are locked again
 * and the sphere returns to the lead's at once. A game's point round
 * frees the camera only (`setExploring(true, false)`).
 */

import { connectRoom, emptyRoomStatus, type ActivityData, type RoomClientHandle, type RoomStatus } from './roomClient'
import { ROOM_MAX_MESSAGE_CHARS, type RoomClientMessage, type RoomColours, type RoomDataPins, type RoomSatellites, type RoomGlobe, type RoomLayers, type RoomNote, type RoomPlayback, type RoomSlotUpdate, type RoomState, type RoomView } from './roomProtocol'
import { dataPinsKey, fitLook } from './roomLook'
import { leadSimMs, roomSatellitesKey } from './satellites/satelliteRoom'

/** The app, as far as a shared session needs it. */
export interface RoomSyncHost {
  getDatasetId(): string | null
  /**
   * Load the lead's dataset. AYNI: `false` means "not yet" — this device's
   * catalogue has not loaded it (a room joins before the catalogue arrives)
   * — and the next message asks again instead of waiting out the retry.
   */
  loadDataset(id: string): boolean | void
  /** The primary dataset's video, when it has one. */
  getVideo(): HTMLVideoElement | null
  isPlaying(): boolean
  /**
   * Be playing, or be paused: the app's state and its video element both.
   * Idempotent, so a follower may ask on every tick; a video resting on
   * its last frame between loops counts as playing.
   */
  setPlaying(playing: boolean): void
  /** The sphere in the AR session; null outside one. */
  getGlobe(): RoomGlobe | null
  /** Make the AR sphere follow this pose; null hands it back to the user. */
  setFollowedGlobe(globe: RoomGlobe | null): void
  /** The browser globe's camera. */
  getView(): RoomView | null
  /** Move the browser globe's camera there. */
  setView(view: RoomView): void
  /** The layer stack around the dataset. */
  getLayers(): RoomLayers | null
  /** Put that layer stack on; a no-op when it already is. */
  setLayers(layers: RoomLayers): void
  /**
   * Take the controls out of this device's hands, or give them back:
   * the globe, and everything that changes what is on it (datasets,
   * playback, layers). Locked for a meeting's audience and moderator
   * while a presenter is leading: there the sphere is the presenter's.
   */
  setLocked(locked: boolean, driving?: number | null): void
  /** Unload the dataset: the lead is back on the bare Earth. */
  clearDataset(): void
  /**
   * The lead's explore activity began or ended on this follower: give the
   * globe back to this device for it, or take it again. `free` (an explore
   * activity) gives back every control — datasets, time, layers, Tools —
   * and the sphere is not steered meanwhile; without it (a game's point
   * round) only the camera. Optional; without it a locked audience simply
   * stays locked.
   */
  setExploring?(exploring: boolean, free?: boolean): void
  /** AYNI: what this device's Khipu shows the room (its step, captions and pins), or null. Optional. */
  getNote?(): RoomNote | null
  /** AYNI: show the lead's Khipu note on this follower, or take it away (null). Optional. */
  setFollowedNote?(note: RoomNote | null): void
  /**
   * AYNI (owner, 2026-10-22): the colours this device draws its datasets in
   * (the main globe's, the other globes', the real-time overlays'). Optional.
   */
  getColours?(): RoomColours[]
  /**
   * A follower: draw the lead's colours, without saving them as this
   * viewer's own; null (no longer following) puts this viewer's own back.
   * Asked on every tick once the lead's dataset is up: a no-op when they match.
   */
  setFollowedColours?(colours: RoomColours[] | null): void
  /** AYNI (owner, 2026-10-22): this device's data pins and whether its pin graph is on screen; undefined for neither. Optional. */
  getDataPins?(): RoomDataPins | undefined
  /**
   * A follower: show the lead's pins (an empty list: the lead has none) and
   * its graph; null (no longer following) puts this viewer's own pins back.
   * Asked on every tick: a no-op when they match.
   */
  setFollowedPins?(pins: RoomDataPins | null): void
  /** AYNI (satellites plan): this device's satellites (undefined while off). Optional. */
  getSatellites?(): RoomSatellites | undefined
  /**
   * A follower: show the lead's satellites (null: the lead shows none), its
   * state received at `receivedAt`; asked on every tick, a no-op when they match.
   */
  setFollowedSatellites?(satellites: RoomSatellites | null, receivedAt: number): void
  /** Leaving the room: this viewer's own satellite choices back. */
  restoreOwnSatellites?(): void
  /**
   * A follower has become the lead: the pins and colours it took from the
   * old lead are its own now, shown on, and not given back when it leaves.
   */
  keepFollowed?(): void
  /**
   * AYNI (multi-globe plan, phase 3): the globes this page shows when there
   * are more than one — the layout, the main one, and each by position (its
   * dataset, its time rule, its camera); null on one globe. Drivers are
   * filled in by the sync from the room's roster.
   */
  getSlots?(): RoomSlots | null
  /**
   * A follower: show the lead's globes — the layout, the main one, each
   * globe's dataset and time rule — leaving the globe this device drives
   * (`driving`) as it is. Idempotent: asked on every state.
   */
  setSlots?(state: RoomState, driving: number | null): void
  /** Globe n's camera, dataset and time rule, as a driver reports them. */
  getSlotUpdate?(n: number): RoomSlotUpdate | null
  /** Put a driver's (or the lead's) camera, dataset or time rule on globe n. */
  setSlotUpdate?(n: number, update: RoomSlotUpdate): void
  /** Link or unlink the globes' cameras: unlinked while anyone drives a globe. */
  setCamerasLinked?(linked: boolean): void
  /**
   * AYNI (multi-globe plan): with `setLocked(true, n)` the globe this device
   * drives stays in its hands (its camera, its dataset chip, its time chip).
   */
}

/** The lead's globes, as `RoomState` carries them. */
export type RoomSlots = Required<Pick<RoomState, 'layout' | 'primary' | 'slots' | 'linked'>>

/**
 * The note cut until the state carrying it fits one message: pins go
 * first, then lines from the end. Null when even the titles do not fit.
 */
export function fitNote(base: Omit<RoomState, 'note'>, note: RoomNote | null): RoomNote | null {
  if (!note) return null
  // The envelope ({"t":"state","s":…}) and some slack for escaping.
  const budget = ROOM_MAX_MESSAGE_CHARS - 64
  const size = (n: RoomNote): number => JSON.stringify({ ...base, note: n }).length
  let n: RoomNote = { ...note, lines: [...note.lines], pins: [...note.pins] }
  while (size(n) > budget && n.pins.length) n = { ...n, pins: n.pins.slice(0, -1) }
  while (size(n) > budget && n.lines.length) n = { ...n, lines: n.lines.slice(0, -1) }
  return size(n) > budget ? null : n
}

/** Whether `status` has this device exploring on its own: a follower, during an explore activity still in time. */
export function isExploring(status: RoomStatus, nowMs: number = Date.now()): boolean {
  const a = status.activity
  if (status.role === 'follower' && !!a && a.kind === 'explore' && !a.closed && (a.endsAt === null || nowMs < a.endsAt)) return true
  // AYNI (meeting games plan): a player looks for the place on a point round with the globe in their own hands.
  const g = status.game
  // A table game (Wayra, Team Earth): a player turns their own globe all game long, to look at the places.
  if (status.role === 'follower' && g?.phase === 'round' && !!g.table && !g.teacher) return true
  const round = g?.phase === 'round' ? g.round : null
  return status.role === 'follower' && !!round && round.kind === 'point' && round.playing && !round.closed && !g?.teacher
}

/**
 * Whether `status` has this device exploring with the whole sphere in its
 * hands: a follower during an explore activity still in time (not a game's
 * round, which frees the camera only).
 */
export function exploresFreely(status: RoomStatus, nowMs: number = Date.now()): boolean {
  const a = status.activity
  return status.role === 'follower' && !!a && a.kind === 'explore' && !a.closed && (a.endsAt === null || nowMs < a.endsAt)
}

/** How often the lead looks at its own state, ms. */
const LEAD_TICK_MS = 100
/** The lead repeats itself at least this often, changed or not. */
const HEARTBEAT_MS = 1000
/** How often a follower re-checks its playhead against the lead's. */
const FOLLOW_TICK_MS = 250
/** A playing follower within this of the lead is in step, seconds. */
export const IN_STEP_S = 0.1
/** A playing follower seeks only when further than this from the lead, and further than its seeks cost. */
export const SEEK_PLAYING_S = 2.5
/** What a seek is assumed to cost until one has been measured on this device, seconds. */
export const ASSUMED_SEEK_COST_S = 1
/** A video meant to be playing that has not moved for this long is stuck, and is nudged with a seek, ms. */
export const STALL_NUDGE_MS = 4000
/** A seek that has not landed after this long is given up on, ms. */
export const SEEK_GIVE_UP_MS = 15000
/** Between the two it trims its rate: by this much per second of gap… */
const TRIM_PER_SECOND = 0.2
/** …up to this fraction of the lead's rate, which the eye does not notice… */
export const MAX_RATE_TRIM = 0.1
/** …in steps of this much. */
const TRIM_STEP = 0.025
/** A follower at the lead's rate starts trimming only past this gap, seconds. */
const TRIM_START_S = 0.3
/** A paused follower holds the lead's frame to within this. */
const SEEK_PAUSED_S = 0.08
/** Within this of either end a video is left to its own loop: both devices rest on the last frame and start over by the same rule. */
const END_ZONE_S = 0.4
/** After a seek has landed, leave the decoder alone this long. */
const SEEK_SETTLE_MS = 3000
/** A dataset load is not asked for again within this. */
const LOAD_RETRY_MS = 10000

/** Where the lead's playhead is `elapsedS` after it sent `playback`. */
export function leadTime(playback: RoomPlayback, elapsedS: number): number {
  const time = playback.paused ? playback.time : playback.time + Math.max(0, elapsedS) * playback.rate
  // The end is held, not wrapped: the lead rests on its last frame before
  // looping, and says so with a fresh message when it starts over.
  return Math.min(playback.duration, time)
}

export interface PlaybackCorrection {
  /** Flip play/pause. */
  toggle: boolean
  /** Seek the local video here, seconds; null to leave it. */
  seekTo: number | null
  /** Set the local rate; null to leave it. */
  rate: number | null
}

/**
 * What a follower should do to its video to match the lead's. Positions
 * are compared as fractions of each device's own duration, because two
 * devices can pick renditions of slightly different length.
 *
 * Paused, the follower takes the lead's frame. Playing, it is in one of
 * three bands: in step (the lead's own rate), a gap the rate can close
 * (the lead's rate trimmed by up to MAX_RATE_TRIM, faster when behind and
 * slower when ahead), or further out (one seek). Near either end of the
 * video it does nothing: the loop is each device's own.
 *
 * `seekCostS` is how long a seek takes to land on this device, measured
 * by the caller. A seek is the expensive way to catch up — on a stream
 * near the limit of the connection it stalls the video for many seconds,
 * by which time the lead is far ahead again, and a follower that seeks
 * again loops for good — so the seek band starts above what a seek costs,
 * and a seek aims at where the lead will be once it has landed.
 *
 * Gaps are measured round the loop: every device loops the same video, so
 * a follower at the end and a lead just past the start are close.
 */
export function followPlayback(
  playback: RoomPlayback,
  elapsedS: number,
  local: { paused: boolean; time: number; duration: number; rate: number },
  seekCostS: number = ASSUMED_SEEK_COST_S,
): PlaybackCorrection {
  const target = (leadTime(playback, elapsedS) / playback.duration) * local.duration
  const toggle = playback.paused !== local.paused
  const setRate = (rate: number): number | null => (Math.abs(local.rate - rate) > 0.004 ? rate : null)
  const duration = local.duration
  const gap = playback.paused
    ? target - local.time
    : ((((target - local.time + duration / 2) % duration) + duration) % duration) - duration / 2
  if (playback.paused) {
    return {
      toggle,
      seekTo: Math.abs(gap) > SEEK_PAUSED_S ? Math.max(0, Math.min(local.duration, target)) : null,
      rate: setRate(playback.rate),
    }
  }
  const nearEnd = (time: number): boolean => time >= local.duration - END_ZONE_S
  if (nearEnd(target) || nearEnd(local.time)) return { toggle, seekTo: null, rate: setRate(playback.rate) }
  if (Math.abs(gap) <= IN_STEP_S) return { toggle, seekTo: null, rate: setRate(playback.rate) }
  if (Math.abs(gap) > Math.max(SEEK_PLAYING_S, 1.5 * seekCostS + 1)) {
    // Land where the lead will be by then, short of the end so the landing
    // is not straight into the loop.
    const aim = Math.min(duration - END_ZONE_S - 0.1, target + seekCostS * playback.rate)
    return { toggle, seekTo: Math.max(0, aim), rate: setRate(playback.rate) }
  }
  // A follower at the lead's own rate is left there until it is clearly
  // out (TRIM_START_S): every rate change is work for the decoder and the
  // audio path, and a gap that comes and goes is message jitter, not drift.
  const untrimmed = Math.abs(local.rate - playback.rate) <= 0.004
  if (untrimmed && Math.abs(gap) <= TRIM_START_S) return { toggle, seekTo: null, rate: null }
  // In steps, so a gap closing steadily changes the rate a few times, not on every tick.
  const raw = Math.max(-MAX_RATE_TRIM, Math.min(MAX_RATE_TRIM, gap * TRIM_PER_SECOND))
  const trim = Math.sign(raw) * Math.max(TRIM_STEP, Math.round(Math.abs(raw) / TRIM_STEP) * TRIM_STEP)
  const rate = playback.rate * (1 + trim)
  return { toggle, seekTo: null, rate: Math.abs(local.rate - rate) > 0.004 ? rate : null }
}

/**
 * Do two cameras differ by more than a still globe's jitter? Longitude
 * is compared the short way round, and more finely the closer the camera
 * is, since a hundredth of a degree is nothing from orbit and a street
 * from zoom 14.
 */
export function viewsDiffer(a: RoomView, b: RoomView): boolean {
  const fine = 0.5 / 2 ** Math.max(0, Math.min(a.zoom, b.zoom))
  const turn = Math.abs(((((a.lon - b.lon + 180) % 360) + 360) % 360) - 180)
  return (
    Math.abs(a.lat - b.lat) > fine ||
    turn > fine ||
    Math.abs(a.zoom - b.zoom) > 0.01 ||
    Math.abs(((((a.bearing - b.bearing + 180) % 360) + 360) % 360) - 180) > 0.2 ||
    Math.abs(a.pitch - b.pitch) > 0.2 ||
    // AYNI (2026-10-19): the flat map or the globe.
    !!a.flat !== !!b.flat
  )
}

/** The angle between two orientations, radians. */
function turnBetween(a: readonly number[], b: readonly number[]): number {
  const dot = Math.abs(a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3])
  return 2 * Math.acos(Math.min(1, dot))
}

/**
 * Is `next` worth sending, given what was sent `elapsedMs` ago? Yes when
 * a follower could not have predicted it from `prev`, or when the
 * heartbeat is due.
 */
export function shouldSend(prev: RoomState | null, next: RoomState, elapsedMs: number): boolean {
  if (!prev || elapsedMs >= HEARTBEAT_MS) return true
  if (prev.datasetId !== next.datasetId) return true
  if ((prev.playback === null) !== (next.playback === null)) return true
  if (prev.playback && next.playback) {
    if (prev.playback.paused !== next.playback.paused) return true
    if (Math.abs(prev.playback.rate - next.playback.rate) > 0.001) return true
    if (Math.abs(leadTime(prev.playback, elapsedMs / 1000) - next.playback.time) > 0.25) return true
  }
  if ((prev.globe === null) !== (next.globe === null)) return true
  if ((prev.view === null) !== (next.view === null)) return true
  if (prev.view && next.view && viewsDiffer(prev.view, next.view)) return true
  if (JSON.stringify(prev.layers) !== JSON.stringify(next.layers)) return true
  if (JSON.stringify(prev.note ?? null) !== JSON.stringify(next.note ?? null)) return true
  // AYNI (owner, 2026-10-22): the colours, and the pins (a following pin's place is not a change: dataPinsKey).
  if (JSON.stringify(prev.colours ?? []) !== JSON.stringify(next.colours ?? [])) return true
  if (dataPinsKey(prev.dataPins) !== dataPinsKey(next.dataPins)) return true
  // AYNI (satellites plan): the lead's satellites; its clock only when it drifts from where it was going.
  if (roomSatellitesKey(prev.satellites) !== roomSatellitesKey(next.satellites)) return true
  if (prev.satellites && next.satellites && !next.satellites.live && Math.abs(leadSimMs(prev.satellites, elapsedMs, 0) - (next.satellites.simMs ?? 0)) > 2000) return true
  // AYNI (multi-globe plan): another globe's dataset, rule, driver or (unlinked) camera.
  if (prev.layout !== next.layout || prev.primary !== next.primary || (prev.linked ?? true) !== (next.linked ?? true)) return true
  if ((prev.slots?.length ?? 0) !== (next.slots?.length ?? 0)) return true
  for (let i = 0; i < (next.slots?.length ?? 0); i++) {
    const a = prev.slots![i]
    const b = next.slots![i]
    if (a.datasetId !== b.datasetId || (a.time ?? '') !== (b.time ?? '') || (a.driver ?? null) !== (b.driver ?? null)) return true
    if (JSON.stringify(a.layers ?? null) !== JSON.stringify(b.layers ?? null)) return true
    if ((a.view === null || a.view === undefined) !== (b.view === null || b.view === undefined)) return true
    if (a.view && b.view && viewsDiffer(a.view, b.view)) return true
  }
  if (prev.globe && next.globe) {
    if (prev.globe.aligned !== next.globe.aligned) return true
    if (Math.abs(prev.globe.scale - next.globe.scale) > 0.005 * prev.globe.scale) return true
    if (turnBetween(prev.globe.q, next.globe.q) > 0.005) return true
  }
  return false
}

export interface RoomSyncHandle {
  status(): RoomStatus
  /** This device's own state right now, as a lead would report it. */
  snapshot(): RoomState
  /** Called now with the current status, and on every change. Returns an unsubscribe. */
  onStatus(listener: (status: RoomStatus) => void): () => void
  /** The heat map, pins and tally (lead and moderator only). Returns an unsubscribe. */
  onActivityData(listener: (data: ActivityData) => void): () => void
  /** Send an activity message to the room. */
  send(message: Exclude<RoomClientMessage, { t: 'state' }>): void
  stop(): void
}

export interface RoomSyncOptions {
  /** Clock, for tests. */
  nowMs?: () => number
  /** Build the connection, for tests. */
  connect?: typeof connectRoom
  /** The signed link's token, when the page was opened from one. */
  token?: string | null
}

let current: RoomSyncHandle | null = null

/** The running shared session, if this page is in one. */
export function activeRoomSync(): RoomSyncHandle | null {
  return current
}

/** Join room `code` and keep this device in step with it. */
export function startRoomSync(code: string, host: RoomSyncHost, opts: RoomSyncOptions = {}): RoomSyncHandle {
  current?.stop()
  const nowMs = opts.nowMs ?? (() => performance.now())
  const listeners = new Set<(status: RoomStatus) => void>()
  let status: RoomStatus = emptyRoomStatus(code)

  // --- follower side ---
  let followed: { state: RoomState; at: number } | null = null
  let lastSeekAt = -Infinity
  let loadAsked: { id: string; at: number } | null = null
  /** How long seeks take to land on this device, from the last one measured. */
  let seekCostS = ASSUMED_SEEK_COST_S
  /** A seek in flight: when it was issued. */
  let seekIssuedAt: number | null = null
  /** The lead's own rate, to put back when this device stops following: a trimmed rate is not this video's rate. */
  let untrimmedRate: number | null = null
  /** Where the video last was, and since when: how a stuck one is told from a moving one. */
  let progress = { time: -1, at: 0 }
  /** Whether this follower is exploring on its own right now, and whether with the whole sphere (`free`). */
  let exploring = false
  let free = false
  const dataListeners = new Set<(data: ActivityData) => void>()

  /** Start or stop exploring when the activity or the clock says so. */
  function syncExploring(): void {
    // A dropped connection keeps the globe as it was: the room says on reconnecting whether the exploring goes on.
    if (!status.connected) return
    const now = isExploring(status)
    const nowFree = now && exploresFreely(status)
    if (now === exploring && nowFree === free) return
    const wasFree = free
    exploring = now
    free = nowFree
    host.setExploring?.(now, nowFree)
    if (nowFree && !wasFree) {
      // The sphere is this device's own: its AR pose and its video's own rate.
      host.setFollowedGlobe(null)
      restoreRate()
    }
    if (wasFree && !nowFree) {
      // Back from exploring freely: the lead's dataset, time and layers are
      // asked for again at once, and its globes and AR pose taken.
      loadAsked = null
      if (followed) {
        host.setFollowedGlobe(followed.state.globe)
        applySlots(followed.state)
      }
    }
    // Back from exploring: to the lead's view at once, not on the lead's next move.
    if (!now && followed?.state.view) host.setView(followed.state.view)
  }

  function restoreRate(): void {
    const video = host.getVideo()
    if (video && untrimmedRate !== null) video.playbackRate = untrimmedRate
    untrimmedRate = null
  }

  function follow(): void {
    syncExploring()
    // Exploring freely: what is on this sphere is this device's own until the activity ends.
    if (free) return
    if (status.role !== 'follower' || !followed) return
    const { state, at } = followed
    const now = nowMs()
    // AYNI (owner, 2026-10-22): the lead's pins and graph, whatever is on the sphere yet.
    host.setFollowedPins?.(state.dataPins ?? { pins: [] })
    // AYNI (satellites plan): the lead's satellites and their clock.
    host.setFollowedSatellites?.(state.satellites ?? null, at)
    if (state.datasetId === null && host.getDatasetId() !== null) {
      // The lead went back to the bare Earth.
      if (!loadAsked || loadAsked.id !== '' || now - loadAsked.at > LOAD_RETRY_MS) {
        loadAsked = { id: '', at: now }
        host.clearDataset()
      }
      return
    }
    if (state.datasetId && state.datasetId !== host.getDatasetId()) {
      if (!loadAsked || loadAsked.id !== state.datasetId || now - loadAsked.at > LOAD_RETRY_MS) {
        if (host.loadDataset(state.datasetId) !== false) loadAsked = { id: state.datasetId, at: now }
      }
      return // nothing to steer until the same dataset is on the sphere
    }
    // The same dataset is up: its surroundings next. (The host makes
    // this a no-op once they match, so the tick may ask every time.)
    if (state.layers) host.setLayers(state.layers)
    // AYNI (owner, 2026-10-22): and the lead's colours on them.
    host.setFollowedColours?.(state.colours ?? [])
    const video = host.getVideo()
    if (!state.playback || !video || !(video.duration > 0) || video.readyState < 2) return
    const playing = host.isPlaying()
    // A video that is mid-seek, still buffering, or not advancing though
    // the app has it playing (resting on its last frame between loops, or
    // held by the browser until the page is tapped) has no position worth
    // correcting. Steering it would be a seek every few seconds on a
    // picture that is not moving: a slideshow.
    const advancing = !video.seeking && video.readyState >= 3 && !(playing && video.paused)
    if (video.currentTime !== progress.time) progress = { time: video.currentTime, at: now }
    if (seekIssuedAt !== null && now - seekIssuedAt > SEEK_GIVE_UP_MS) {
      // A seek that never lands must not hold the follower forever.
      seekCostS = Math.max(seekCostS, SEEK_GIVE_UP_MS / 1000)
      seekIssuedAt = null
      lastSeekAt = now
    }
    if (seekIssuedAt !== null && advancing) {
      // A seek is over once the video plays on from it: now its cost is
      // known, and it is known before the next decision is made with it.
      seekCostS = Math.max(0.3, (now - seekIssuedAt) / 1000)
      seekIssuedAt = null
      lastSeekAt = now
    }
    const fix = followPlayback(
      state.playback,
      (now - at) / 1000,
      { paused: !playing, time: video.currentTime, duration: video.duration, rate: video.playbackRate },
      seekCostS,
    )
    // Asked every tick, not toggled on a difference: the app's playing
    // flag and the element can disagree for a moment (a dataset load
    // resets one and not the other), a toggle made in that moment lands
    // the wrong way, and the follower then believed it was playing while
    // its video sat paused for good. setPlaying brings both to the lead's
    // state, and does nothing when they are there already.
    host.setPlaying(!state.playback.paused)
    // Stuck: meant to be playing, not paused, not seeking, and not moving.
    // A stream can sit at its start like that until something seeks it
    // (playbackController's own start-up nudge exists for the same
    // reason), and a follower that only corrects a moving video would wait
    // there for good. So it is nudged, with a seek to where the lead is.
    const stuck =
      !state.playback.paused && playing && !video.paused && !video.seeking &&
      now - progress.at > STALL_NUDGE_MS && video.currentTime < video.duration - END_ZONE_S
    if (stuck && seekIssuedAt === null && now - lastSeekAt > SEEK_SETTLE_MS) {
      const target = (leadTime(state.playback, (now - at) / 1000) / state.playback.duration) * video.duration
      lastSeekAt = now
      seekIssuedAt = now
      progress = { time: video.currentTime, at: now }
      video.currentTime = Math.max(0, Math.min(video.duration - END_ZONE_S - 0.1, target + seekCostS * state.playback.rate))
      return
    }
    // Not while a seek is still landing, nor on a video that is not moving.
    if (seekIssuedAt !== null) return
    if (!state.playback.paused && !advancing) return
    if (fix.rate !== null) {
      untrimmedRate = state.playback.rate
      video.playbackRate = fix.rate
    }
    if (fix.seekTo !== null && !video.seeking && now - lastSeekAt > SEEK_SETTLE_MS) {
      lastSeekAt = now
      // A paused video lands at once; only a playing one has a cost to learn.
      if (!state.playback.paused) seekIssuedAt = now
      video.currentTime = fix.seekTo
    }
  }

  // --- lead side ---
  let sent: { state: RoomState; at: number } | null = null

  function readState(): RoomState {
    const video = host.getVideo()
    // What the video is doing, not what the app believes: the app can say
    // playing while its video sits paused (a play the browser refused, or
    // one asked for before the video was ready), and a lead reporting that
    // would have every follower chase a playhead that never moves. Resting
    // on the last frame between loops is the one pause that is playing.
    const resting = !!video && video.paused && video.duration > 0 && video.currentTime >= video.duration - END_ZONE_S
    const playback: RoomPlayback | null =
      video && video.duration > 0 && Number.isFinite(video.duration)
        ? {
            paused: !host.isPlaying() || (video.paused && !resting),
            time: Math.max(0, Math.min(video.duration, video.currentTime)),
            duration: video.duration,
            rate: video.playbackRate > 0 ? video.playbackRate : 1,
          }
        : null
    const base: Omit<RoomState, 'note'> = {
      datasetId: host.getDatasetId(),
      playback,
      globe: host.getGlobe(),
      view: host.getView(),
      layers: host.getLayers(),
    }
    // AYNI (multi-globe plan): the other globes, with their drivers from the roster; only on more than one globe.
    const globes = host.getSlots?.() ?? null
    if (globes && globes.layout !== '1' && globes.slots.length > 1) {
      base.layout = globes.layout
      base.primary = globes.primary
      base.linked = status.drivers.length === 0
      base.slots = globes.slots.map((s, i) => {
        const driver = status.drivers.find((d) => d.slot === i)?.id ?? null
        return {
          datasetId: s.datasetId,
          ...(s.time ? { time: s.time } : {}),
          driver,
          ...(!base.linked && !driver && i !== globes.primary && s.view ? { view: s.view } : {}),
          // AYNI (multi-globe plan, phase 2): the globe's own layers (the main globe's are the state's).
          ...(i !== globes.primary && s.layers !== undefined ? { layers: s.layers } : {}),
        }
      })
    }
    // AYNI (owner, 2026-10-22): the colours and the pins, before the note, which is cut to what is left.
    // AYNI (satellites plan): the lead's satellites (small, so before the colours and pins are fitted).
    const satellites = host.getSatellites?.()
    if (satellites) base.satellites = satellites
    Object.assign(base, fitLook(base, host.getColours?.() ?? [], host.getDataPins?.()))
    // Only a state with a note says so: without one it keeps the shape every room knows.
    const note = fitNote(base, host.getNote?.() ?? null)
    return note ? { ...base, note } : base
  }

  // --- AYNI (multi-globe plan): a driver's own globe, sent on the lead's tick ---
  let sentSlot: { update: RoomSlotUpdate; at: number } | null = null

  function drive(): void {
    const n = status.driving
    if (n === null || status.role !== 'follower') return
    const update = host.getSlotUpdate?.(n) ?? null
    if (!update) return
    const now = nowMs()
    const prev = sentSlot
    const changed = !prev || now - prev.at >= HEARTBEAT_MS
      || (prev.update.view === null) !== (update.view === null)
      || (!!prev.update.view && !!update.view && viewsDiffer(prev.update.view, update.view))
      || (prev.update.datasetId ?? null) !== (update.datasetId ?? null)
      || (prev.update.time ?? '') !== (update.time ?? '')
    if (!changed) return
    sentSlot = { update, at: now }
    client.sendMessage({ t: 'slot', n, s: update })
  }

  /** A follower takes the lead's globes: the layout and datasets, and the cameras it is not driving. */
  function applySlots(state: RoomState): void {
    if (!host.setSlots) return
    host.setSlots(state, status.driving)
    host.setCamerasLinked?.(state.linked !== false || !state.slots)
    if (state.linked === false && state.slots) {
      state.slots.forEach((s, i) => {
        if (i === status.driving || i === (state.primary ?? 0) || s.driver || !s.view) return
        host.setSlotUpdate?.(i, { view: s.view })
      })
    }
  }

  function lead(): void {
    if (status.role !== 'lead') return
    const now = nowMs()
    const state = readState()
    if (!shouldSend(sent?.state ?? null, state, sent ? now - sent.at : Infinity)) return
    sent = { state, at: now }
    client.send(state)
  }

  const client: RoomClientHandle = (opts.connect ?? connectRoom)(code, {
    token: opts.token ?? null,
    onState: (state) => {
      followed = { state, at: nowMs() }
      host.setFollowedNote?.(state.note ?? null)
      syncExploring()
      if (!free) host.setFollowedGlobe(state.globe)
      // The camera is moved when the lead says where it is, not on the
      // follower's own tick: between messages the follower's globe is
      // still, and re-applying a stale view would fight nothing.
      const local = host.getView()
      if (!exploring && state.view && (!local || viewsDiffer(local, state.view))) host.setView(state.view)
      if (!free) applySlots(state)
      follow()
    },
    // AYNI (multi-globe plan): a driver moved its globe, or changed what it shows.
    onSlot: (n, update) => {
      host.setSlotUpdate?.(n, update)
    },
    onStatus: (next) => {
      const was = status.role
      const drove = status.driving
      status = next
      if (next.role !== 'follower') {
        // Leading, or cut off: the sphere is this device's own again.
        followed = null
        host.setFollowedGlobe(null)
        host.setFollowedNote?.(null)
        restoreRate()
      }
      // A device that has just become the lead says where things stand at once.
      if (next.role === 'lead' && was !== 'lead') sent = null
      // AYNI (owner, 2026-10-22): and keeps what it showed of the old lead's, so the room sees no jump.
      if (next.role === 'lead' && was === 'follower') host.keepFollowed?.()
      // Given a globe: say where it is at once. Nobody driving: the cameras link again.
      if (next.driving !== drove) sentSlot = null
      if (next.role === 'lead') host.setCamerasLinked?.(next.drivers.length === 0)
      if (!next.connected || !next.meeting) host.setCamerasLinked?.(true)
      // In a meeting everyone but the presenter is locked, from the moment
      // they arrive: in the lobby too, so nobody rearranges a sphere the
      // presenter is about to take over. An open room locks nobody. A
      // participant the presenter has handed the globe to leads, unlocked;
      // one given a globe keeps that globe (AYNI, multi-globe plan).
      host.setLocked(next.meeting && next.seat !== null && next.seat !== 'presenter' && next.role !== 'lead', next.driving)
      syncExploring()
      for (const listener of listeners) listener({ ...status })
    },
    onActivityData: (data) => {
      for (const listener of dataListeners) listener(data)
    },
  })

  const leadTimer = setInterval(() => {
    lead()
    drive()
  }, LEAD_TICK_MS)
  const followTimer = setInterval(follow, FOLLOW_TICK_MS)

  const handle: RoomSyncHandle = {
    status: () => ({ ...status }),
    snapshot: readState,
    onStatus(listener) {
      listeners.add(listener)
      listener({ ...status })
      return () => listeners.delete(listener)
    },
    onActivityData(listener) {
      dataListeners.add(listener)
      return () => dataListeners.delete(listener)
    },
    send(message) {
      client.sendMessage(message)
    },
    stop() {
      clearInterval(leadTimer)
      clearInterval(followTimer)
      client.close()
      host.setFollowedGlobe(null)
      host.setFollowedNote?.(null)
      // AYNI (owner, 2026-10-22): this viewer's own pins and colours back.
      host.setFollowedPins?.(null)
      host.setFollowedColours?.(null)
      host.restoreOwnSatellites?.()
      host.setLocked(false, null)
      host.setCamerasLinked?.(true)
      restoreRate()
      listeners.clear()
      dataListeners.clear()
      if (exploring) host.setExploring?.(false, false)
      exploring = false
      free = false
      if (current === handle) current = null
    },
  }
  current = handle
  return handle
}
